import {
  FilterDefinition,
  buildFilterOptions,
  deserializeFilterQueryParamValues,
  filterItems,
  serializeFilterQueryParamValues
} from "@swivl-mono/helpers";
import { LocationInterface, UnitInterface, UserInterface, UserVarKey, UserVarsMap } from "@swivl-mono/types";
import { FilterDropdown, SearchInput } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";

import { ActiveFilterChipSection } from "../../../../../Components/ActiveFilterChipSection/ActiveFilterChipSection";
import { DetailsPanel } from "../../../../../Components/Conversations/DetailsPanel/DetailsPanel";
import { DataCardLocation } from "../../../../../Components/DataCards/DataCard.Location";
import Pill, { PillSize, PillStyle, PillType } from "../../../../../Components/Pill/Pill";
import useDebounce from "../../../../../Hooks/useDebounce";
import { useUrlState } from "../../../../../Hooks/useUrlState";
import LocationsModel from "../../../../../Models/Locations/Locations.Model";
import TenantsModel from "../../../../../Models/Tenants/Tenants.Model";
import { UnitsModel } from "../../../../../Models/Units/Units.Model";
import { UserDetailsLocationDetailsView } from "./UserDetails.LocationDetailsView";
import { UserDetailsUnitDetailsView } from "./UserDetails.UnitDetailsView";

interface UserDetailsLocationLookupViewProps {
  user: UserInterface;
  userVars: UserVarsMap | undefined;
}

export function UserDetailsLocationLookupView({ user, userVars }: UserDetailsLocationLookupViewProps) {
  const panelTabs = ["Store Details", "Unit Details"];
  const { groups } = LocationsModel.useState((s) => s);
  const { searchParams, setMultipleSearchParams } = useUrlState();
  const selectedLocationIdParam = searchParams.get("locationId");
  const selectedPanelTab = searchParams.get("tab");
  const defaultPanelTabIndex = selectedPanelTab ? panelTabs.indexOf(selectedPanelTab) : 0;
  const selectedLocationId = selectedLocationIdParam ? Number(selectedLocationIdParam) : undefined;
  const locationId = user?.locationId
    ? user.locationId
    : userVars && userVars[UserVarKey.Location]
      ? parseInt(userVars[UserVarKey.Location])
      : undefined;
  const unitsByLocation = UnitsModel.useState((s) => s.unitsByLocation);
  const searchParamsKey = searchParams.toString();
  const [openPanel, setOpenPanel] = useState(false);
  const [filteredLocationsByGroup, setFilteredLocationsByGroup] = useState(groups || []);
  const [search, setSearch] = useState("");
  const { debouncedValue: debouncedSearch } = useDebounce(search, 300);
  const filterDefinitions: FilterDefinition<UnitInterface>[] = [
    {
      id: "sizes",
      label: "Sizes",
      getValues: (units) => units.map((unit) => unit.dimensions?.replace(/\s*x\s*/i, "x")),
      formatValue: (value) => value.replace("x", " x "),
      sort: (a, b) => {
        const [aWidth, aHeight] = a.split("x").map((dimension) => Number(dimension.trim()));
        const [bWidth, bHeight] = b.split("x").map((dimension) => Number(dimension.trim()));
        return aWidth - bWidth || aHeight - bHeight;
      }
    },
    { id: "amenities", label: "Amenities", getValues: (units) => units?.flatMap((unit) => unit?.features?.map((amenity) => amenity)) || [] }
  ];

  const initialFilterOptions = useMemo(() => {
    if (!unitsByLocation) return [];

    const allUnits = Object.values(unitsByLocation).flat();
    if (!allUnits.length) return [];

    return buildFilterOptions(allUnits, filterDefinitions).filter((group) => group.options.length > 0);
  }, [unitsByLocation]);

  const filterParamKeys = useMemo(() => {
    return filterDefinitions.map((definition) => definition.id);
  }, []);

  const legacyFiltersParam = searchParams.get("filters");
  const legacySelectionsByGroup = useMemo(() => {
    const selections: Record<string, string[]> = {};

    if (!legacyFiltersParam) {
      return selections;
    }

    try {
      const parsedFilters = JSON.parse(legacyFiltersParam);
      if (!Array.isArray(parsedFilters)) {
        return selections;
      }

      for (const group of parsedFilters) {
        if (!group || typeof group !== "object" || typeof group.id !== "string") {
          continue;
        }

        const selectedValues = new Set<string>();

        if (Array.isArray(group.value)) {
          for (const value of group.value) {
            selectedValues.add(String(value));
          }
        }

        if (Array.isArray(group.filters)) {
          for (const filter of group.filters) {
            if (filter?.checked && filter.value !== undefined) {
              selectedValues.add(String(filter.value));
            }
          }
        }

        if (selectedValues.size > 0) {
          selections[group.id] = Array.from(selectedValues);
        }
      }
    } catch {
      return selections;
    }

    return selections;
  }, [legacyFiltersParam]);

  const selectedFiltersByGroup = useMemo(() => {
    const selections: Record<string, string[]> = {};

    for (const key of filterParamKeys) {
      const paramValue = searchParams.get(key);
      if (paramValue) {
        selections[key] = deserializeFilterQueryParamValues(paramValue);
      } else if (legacySelectionsByGroup[key]?.length) {
        selections[key] = legacySelectionsByGroup[key];
      }
    }

    return selections;
  }, [searchParamsKey, filterParamKeys, legacySelectionsByGroup]);

  const parsedInitialFilters = useMemo(() => {
    if (!initialFilterOptions.length) {
      return [];
    }

    // Build columnFilters format for location filtering
    return Object.keys(selectedFiltersByGroup)
      .filter((key) => selectedFiltersByGroup[key]?.length > 0)
      .map((key) => ({
        id: key,
        value: selectedFiltersByGroup[key]
      }));
  }, [initialFilterOptions, selectedFiltersByGroup]);

  const handleChangeFilters = (groupKey: string, filters: string[]) => {
    const updates: Record<string, string | undefined> = {};
    updates[groupKey] = filters.length > 0 ? serializeFilterQueryParamValues(filters) : undefined;
    setMultipleSearchParams(updates);
  };

  const handleClearAllFilters = () => {
    const updates: Record<string, string | undefined> = { filters: undefined };
    for (const key of filterParamKeys) {
      updates[key] = undefined;
    }
    setMultipleSearchParams(updates);
  };

  const locationFilterMap = useMemo(
    () => ({
      sizes: (location: LocationInterface, values: any[]) => {
        const units = unitsByLocation?.[location.id] || [];
        return units.some((unit) => values.includes(unit.dimensions?.replace(/\s*x\s*/i, "x")));
      },
      amenities: (location: LocationInterface, values: any[]) => {
        const units = unitsByLocation?.[location.id] || [];
        return units.some((unit) => unit.features?.some((feature) => values.includes(feature)));
      }
    }),
    [unitsByLocation]
  );

  useEffect(() => {
    if (!groups || !locationFilterMap) return;

    const newGroups = groups.map((group) => {
      let locations = filterItems(group.locations, parsedInitialFilters, locationFilterMap);

      if (debouncedSearch.trim()) {
        locations = locations.filter((location) => location.name.toLowerCase().includes(debouncedSearch.toLowerCase()));
      }

      return { ...group, locations };
    });

    setFilteredLocationsByGroup(newGroups);
  }, [groups, parsedInitialFilters, locationFilterMap, debouncedSearch]);

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
    TenantsModel.getTenantsByUserId(user.id);
    if (selectedLocationId) {
      UnitsModel.getUnitsByLocation(selectedLocationId);
    } else if (locationId) {
      UnitsModel.getUnitsByLocation(locationId);
    }
  }, [locationId, selectedLocationId, user.id]);

  // load units for every visible location so filter options are fully populated.
  // getUnitsByLocation is cached — won't re-fetch if already loaded.
  useEffect(() => {
    if (!groups) return;
    for (const group of groups) {
      for (const location of group.locations) {
        UnitsModel.getUnitsByLocation(location.id);
      }
    }
  }, [groups]);

  // keep panel state in sync with deep-linked location param
  useEffect(() => {
    setOpenPanel(!!selectedLocationIdParam);
  }, [selectedLocationIdParam]);

  // reset search params on component mount to ensure no stale params
  useEffect(() => {
    setMultipleSearchParams({
      order: undefined,
      filters: undefined,
      tableValue: undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenPanel = (locationId: number) => {
    setMultipleSearchParams({ locationId: String(locationId), tab: undefined, unitId: undefined });
    setOpenPanel(true);
  };

  const handleClosePanel = () => {
    setMultipleSearchParams({ locationId: undefined, tab: undefined, unitId: undefined });
    setOpenPanel(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 rounded-md bg-white p-4">
        <div className="flex items-center gap-2">
          <FilterDropdown
            id="location-filters"
            filterOptions={initialFilterOptions}
            selectedFilters={selectedFiltersByGroup}
            onChangeFilters={handleChangeFilters}
            onClearAllFilters={handleClearAllFilters}
          />

          <SearchInput
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search store names or location"
            value={search}
            onClear={() => setSearch("")}
          />
        </div>

        <div className="flex items-center gap-3">
          <ActiveFilterChipSection
            filterOptions={initialFilterOptions}
            selectedFiltersByGroup={selectedFiltersByGroup}
            handleClearFilter={(groupKey: string, value: string) => {
              if (value === "") {
                // clear entire group
                handleChangeFilters(groupKey, []);
              } else {
                // clear specific filter value
                const currentFilters = selectedFiltersByGroup[groupKey] ?? [];
                const nextFilters = currentFilters.filter((f) => f !== value);
                handleChangeFilters(groupKey, nextFilters);
              }
            }}
            handleClearFilters={handleClearAllFilters}
            handleSetFilters={handleChangeFilters}
          />
        </div>
      </div>

      {/* group name card & location card list */}
      {Array.isArray(filteredLocationsByGroup) &&
        filteredLocationsByGroup.length > 0 &&
        filteredLocationsByGroup.map((group) => {
          return group.locations.length > 0 ? (
            <div key={group.id} className="mb-3 flex flex-col divide-y divide-grey-300">
              <div className="sticky top-12 z-10 rounded-t-md bg-white px-4 py-4 font-semibold text-grey-600">{group.name}</div>
              {group.locations.map((location: LocationInterface) => (
                <button
                  className="w-full cursor-pointer overflow-hidden last:rounded-b-md"
                  onClick={() => handleOpenPanel(location.id)}
                  key={location.id}
                >
                  <DataCardLocation
                    classNames={{ wrapper: "hover:bg-orange-50" }}
                    title={location.name}
                    isRounded={false}
                    locationId={location.id}
                    variant="secondary"
                    pills={
                      userVars &&
                      Number(userVars?.[UserVarKey.Location]) === location.id && [
                        <Pill key={`pill-${location.id}`} type={PillType.ORANGE} size={PillSize.SMALL} style={PillStyle.LIGHT} text="Selected" />
                      ]
                    }
                  />
                </button>
              ))}
            </div>
          ) : null;
        })}

      <DetailsPanel
        defaultTabIndex={defaultPanelTabIndex >= 0 ? defaultPanelTabIndex : 0}
        isOpen={openPanel}
        onClose={handleClosePanel}
        onTabChange={(tab) => {
          // if navigating to store details tab, clear unitId param to prevent invalid state
          const shouldClearUnitId = tab === "Store Details" && !!searchParams.get("unitId");
          const shouldUpdateTab = searchParams.get("tab") !== tab;

          if (shouldUpdateTab || shouldClearUnitId) {
            const updates: Record<string, string | undefined> = { tab };

            if (shouldClearUnitId) {
              updates.unitId = undefined;
            }

            setMultipleSearchParams(updates);
          }
        }}
        renderTab={(tab, navigateForward) => {
          switch (tab) {
            case "Store Details":
              return <UserDetailsLocationDetailsView navigateForward={navigateForward} user={user} userVars={userVars} />;
            // case "Location Variables List View":
            //   return null;
            case "Unit Details":
              return <UserDetailsUnitDetailsView navigateForward={navigateForward} user={user} userVars={userVars} />;
            default:
              return null;
          }
        }}
        tabs={panelTabs}
      />
    </div>
  );
}
